import { BackIcon, HelpIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Group } from '@components/layout';
import { Modal } from '@components/modal';
import { Typography } from '@components/typography';
import { useCreateWorkspaceMutation } from '@generated/graphql';
import { useUserPreferences } from '@hooks/useUserPreferences';
import { Divider, Stack, useTheme } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import { navigate } from 'gatsby';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { GettingStartedAction } from './GettingStartedAction';

export const GettingStartedProvider = () => {
  const {
    hasSeenGettingStarted,
    hasSeenAgentStudioGettingStarted,
    preferencesLoaded,
    userLoading,
    markGettingStartedAsSeen,
    markAgentStudioGettingStartedAsSeen,
  } = useUserPreferences();
  const { organization } = useOrganizationContext();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Wait for user data and preferences to be fully loaded before deciding
    if (userLoading || !preferencesLoaded || !organization) return;

    // Determine which "has seen" flag to check based on organization type
    // For new users, these will be undefined - treat undefined/false as "hasn't seen" -> show modal
    const hasSeenModal = organization.serviceOnly
      ? hasSeenAgentStudioGettingStarted
      : hasSeenGettingStarted;

    setOpen(!hasSeenModal);
  }, [
    hasSeenGettingStarted,
    hasSeenAgentStudioGettingStarted,
    organization,
    userLoading,
    preferencesLoaded,
  ]);

  const onClose = () => {
    setOpen(false);
    if (organization?.serviceOnly) {
      void markAgentStudioGettingStartedAsSeen();
    } else {
      void markGettingStartedAsSeen();
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeOnNavigate={false} sx={{ py: 12 }}>
      <GettingStartedModal onClose={onClose} agentStudio={organization?.serviceOnly} />
    </Modal>
  );
};

export const GettingStartedModal = ({
  onClose,
  agentStudio = false,
}: {
  onClose: VoidFunction;
  agentStudio?: boolean;
}) => {
  const { palette } = useTheme();
  const { organizationId } = useOrganizationContext();
  const { createAsyncNotification } = useNotifications();
  const [createWorkspaceMutation] = useCreateWorkspaceMutation();
  const [state, setState] = useState<'default' | 'video'>('default');

  return (
    <>
      <Stack gap={5} color={palette.text.paper}>
        {state === 'video' ? (
          <iframe
            style={{ width: '80vw', height: '45vw' }}
            src={
              agentStudio
                ? 'https://rendered.ai/wp-content/uploads/2025/10/Agent-Studio_hot-reel_10.20251.mp4'
                : 'https://www.youtube.com/embed/f7_1YnUvJwQ?si=-dS61QgiSiF-NfyU'
            }
            title={agentStudio ? 'Agent studio tutorial' : 'Synthetic data studio tutorial'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <>
            <Stack gap={1}>
              <Typography variant="h1">
                Getting started with {agentStudio ? 'Agent studio' : 'Synthetic data studio'}
              </Typography>
              <Typography
                variant="body2"
                color={palette.text.primary}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                Return to this window any time from the Help{' '}
                <HelpIcon size={16} color={palette.text.primary} /> menu
              </Typography>
            </Stack>
            <Divider flexItem />
            <Group gap={12} px={9}>
              {agentStudio ? (
                <>
                  <GettingStartedAction
                    imageUrl="/welcome-agent-studio-examples-thumbnail.png"
                    title="Explore example workspaces"
                    description="Browse our marketplace to find pre-configured workspace templates that adapt agents to your specific workflow. Start building faster with proven examples."
                    action={() => {
                      posthog.capture('user_clicked_welcome-agent-studio-example-workspaces');
                      onClose();
                      void navigate('examples/workspaces');
                    }}
                    actionLabel="Browse examples"
                  />
                  <GettingStartedAction
                    imageContent={
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        gap={3}
                        height="100%"
                        width="100%"
                        sx={{
                          background:
                            'radial-gradient(circle at 20% 50%, rgba(118, 185, 0, 0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(118, 185, 0, 0.1), transparent 50%), radial-gradient(circle at 40% 20%, rgba(118, 185, 0, 0.08), transparent 60%);',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '2.5rem',
                            fontWeight: 'bold',
                            color: palette.primary.main,
                          }}
                        >
                          $250
                        </span>
                        <span
                          style={{
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            color: palette.text.primary,
                          }}
                        >
                          starting credits
                        </span>
                      </Stack>
                    }
                    title="Understand cost & credits"
                    description="Compute and storage incur costs as you work. Your organization starts with $250.00 in credits to explore and build with Agent studio. You can track these costs in the billing section of the settings page."
                    action={() => {
                      posthog.capture('user_clicked_welcome-agent-studio-pricing');
                      onClose();
                      const docsBaseUrl = `https://docs.${process.env.GATSBY_ENV === 'prod' ? '' : `${process.env.GATSBY_ENV}.`}rendered.ai`;
                      window.open(`${docsBaseUrl}/agent-studio/costs-and-billing`, '_blank');
                    }}
                    actionLabel="View pricing"
                  />
                  <GettingStartedAction
                    imageUrl="/welcome-video-thumbnail.png"
                    title="Watch the tutorial"
                    description="Get a quick, guided tour of how to use the Agent studio platform. Learn key features and best practices for agent development."
                    action={() => {
                      posthog.capture('user_clicked_welcome-agent-studio-tutorial');
                      setState('video');
                    }}
                    actionLabel="Watch now"
                  />
                </>
              ) : (
                <>
                  <GettingStartedAction
                    imageUrl="/welcome-video-thumbnail.png"
                    title="Tutorial video"
                    description="Get a quick, guided tour of how to use the Rendered.ai platform."
                    action={() => {
                      posthog.capture('user_clicked_welcome-video');
                      setState('video');
                    }}
                    actionLabel="Watch now"
                  />
                  <GettingStartedAction
                    imageUrl="/welcome-example-thumbnail.png"
                    title="Try a sample workspace"
                    description="Test drive configurable synthetic data generation in the Satellite RGB Demo workspace."
                    action={createAsyncNotification(
                      'New workspace was created successfully',
                      async () => {
                        posthog.capture('user_clicked_welcome-create-workspace');
                        if (!organizationId) {
                          return Promise.reject(new Error('Unable to create workspace.'));
                        }
                        const { data: createData } = await createWorkspaceMutation({
                          variables: {
                            organizationId,
                            name: 'Example Workspace',
                            code: 'SATRGBV2',
                            channelIds: [],
                            volumeIds: [],
                          },
                        });
                        if (createData?.createWorkspace) {
                          navigate(`/${organizationId}/workspaces/${createData?.createWorkspace}`);
                        }
                        onClose();
                        return Promise.resolve();
                      },
                    )}
                    actionLabel="Access this workspace"
                  />
                  <GettingStartedAction
                    imageUrl="/welcome-sample-gallery-thumbnail.png"
                    title="Browse our sample gallery"
                    description="Explore other sample workspaces to generate synthetic data for different types of sensors and scenarios."
                    action={() => {
                      posthog.capture('user_clicked_welcome-sample-gallery');
                      onClose();
                      void navigate('sample-gallery');
                    }}
                    actionLabel="Start browsing"
                  />
                </>
              )}
            </Group>
          </>
        )}
      </Stack>
      {state === 'video' && (
        <IconButton
          style={{ position: 'absolute', top: 8, left: 8 }}
          onClick={() => setState('default')}
          size={18}
          Icon={BackIcon}
          aria-label="Close"
        />
      )}
    </>
  );
};
